import React from "react"
import HeroBreadcrumb, {
  BreadcrumbSegment,
} from "../../ui/organisms/HeroBreadcrumb"
import { setScrolling } from "../../utils/scrolling"
import EditorialContents from "../editorialPage/components/EditorialContents"
import DesktopOnly from "../../root/punks/mui/visibility/DesktopOnly"
import MobileOnly from "../../root/punks/mui/visibility/MobileOnly"
import { useRouter } from "next/router"
import MobileBreadcrumb from "./components/header/MobileBreadcrumb"
import { useLabels } from "../../features/contents/hooks"
import { trackSearch } from "../../features/tracking"
import {
  CustomBox,
  CustomCol,
  CustomContainer,
  CustomRow,
} from "../../ui/spacings"
import ToggleButton from "../../ui/molecules/ToggleButton"
import Typography from "../../ui/atoms/Typography"
import Stroke from "../../ui/atoms/Stroke"
import { lightestGray } from "../../themes/variables"
import CollapsibleCol from "../../ui/molecules/CollapsibleCol"
import { SerpUi, useAlgoliaSerpUi } from "./context/ui"
import { AlgoliaSerpHits } from "./components/results/AlgoliaSearchResults"
import { AlgoliaResultsPaginator } from "./components/results/AlgoliaResultsPaginator"
import { OrderByDropdown } from "./components/sorting/OrderByDropdown"
import FilterSidebarItems from "./components/filters/FilterSidebarItems"
import classes from "./AlgoliaSerpContainer.module.css"
import SwitchFilters from "./components/filters/SwitchFilters"
import { ResultsStats } from "./components/stats/ResultsStats"
import { NoResults } from "./components/stats/NoResults"
import { CurrentFilterTags } from "./components/filters/CurrentFilterTags"
import { Collapse } from "../../ui/animations/Collapse"
import { OrderByPanel } from "./components/sorting/OrderByPanel"
import MobileFilters from "./components/header/MobileFilters"
import FiltersModal from "./components/modal/FiltersModal"
import { headerShrinkSize } from "../../ui/organisms/HeaderMobile"
import { useLayoutData } from "../../aggregators/rootData"
import { useIsDown } from "../../features/layout/hooks"
import BreadcrumbJsonLD from "../../features/seo/components/BreadcrumbJsonLD"
import DesktopOnlyJs from "../../root/punks/mui/visibility/DesktopOnlyJs"

export interface AlgoliaSerpContainerProps {
  title: string
  subtitle?: React.ReactNode
  description?: React.ReactNode
  descriptionExpandable?: boolean
  intro?: React.ReactNode
  breadcrumb: BreadcrumbSegment[]
  contents?: any
  heroBackgroundImage?: string
  heroBackgroundImageMobile?: string
  hideHero?: boolean
  heroLogo?: React.ReactNode
  heroClassName?: string
  updateUiState: (value: Partial<SerpUi>) => void
  hideDesktopFilters?: boolean
  searchLabel?: React.ReactNode
}

const AlgoliaSerpContainer = ({
  contents,
  breadcrumb,
  title,
  subtitle,
  description,
  descriptionExpandable,
  intro,
  heroBackgroundImage,
  heroBackgroundImageMobile,
  hideHero,
  heroLogo,
  heroClassName,
  updateUiState,
  hideDesktopFilters,
  searchLabel,
}: AlgoliaSerpContainerProps) => {
  const layout = useLayoutData()
  const ui = useAlgoliaSerpUi()
  const handleRouteChange = (newRoute: string) => {
    updateUiState({
      filtersModalOpen: false,
    })
  }

  const isMobile = useIsDown("md")
  const { getLabel } = useLabels()
  const router = useRouter()

  React.useEffect(() => {
    trackSearch()
    router.events.on("routeChangeStart", handleRouteChange)
    return () => {
      router.events.off("routeChangeStart", handleRouteChange)
    }
  }, [])

  React.useEffect(() => setScrolling(!ui.orderByExpanded), [ui.orderByExpanded])

  return (
    <>
      {(breadcrumb?.length ?? 0) > 0 && (
        <BreadcrumbJsonLD segments={breadcrumb} />
      )}
      {hideHero !== true && (
        <>
          <DesktopOnly lg>
            <HeroBreadcrumb
              title={title}
              subtitle={subtitle}
              description={description}
              descriptionExpandable={descriptionExpandable}
              backgroundImage={heroBackgroundImage}
              backgroundImageMobile={heroBackgroundImageMobile}
              segments={breadcrumb}
              logo={heroLogo}
              center
              className={heroClassName}
            />
          </DesktopOnly>
          <MobileOnly lg>
            <MobileBreadcrumb
              title={title}
              subtitle={subtitle}
              logo={heroLogo}
              className={heroClassName}
              breadcrumb={[
                breadcrumb[0],
                breadcrumb[Math.max(1, breadcrumb.length - 2)],
              ].filter((x) => x)}
            />
          </MobileOnly>
        </>
      )}

      {intro}

      <EditorialContents contents={contents?.data?.topContents} />

      {contents?.data?.info?.productListingHidden !== true ? (
        <>
          <MobileOnly
            className="sticky z-10 bg-white"
            style={{
              top: layout.header.mobileTopPadding - headerShrinkSize,
              maxHeight: "calc(100vh + 45px)",
            }}
            lg
          >
            <MobileFilters
              orderByExpanded={ui.orderByExpanded}
              onFiltersClick={() =>
                updateUiState({
                  filtersModalOpen: true,
                })
              }
              onOrderByClick={() =>
                updateUiState({
                  orderByExpanded: !ui.orderByExpanded,
                })
              }
            />
            <Collapse in={ui.orderByExpanded}>
              <div>
                <OrderByPanel updateUiState={updateUiState} />
              </div>
            </Collapse>
          </MobileOnly>

          <>
            <DesktopOnly lg>
              <CustomContainer>
                <CustomBox py={3} flex="row" justifyContent="space-between">
                  <div>
                    {!hideDesktopFilters && (
                      <ToggleButton
                        value={ui.sidebarCollapsed}
                        onChange={(value) =>
                          updateUiState({
                            sidebarCollapsed: value,
                          })
                        }
                      >
                        {getLabel("search", "filters")}
                      </ToggleButton>
                    )}
                    {searchLabel}
                  </div>
                  <div>
                    <CustomBox flex="row" alignItems="center">
                      <div className="mr-4">
                        <Typography variant="body1">
                          {getLabel("search", "order-by")}
                        </Typography>
                      </div>
                      <OrderByDropdown />
                    </CustomBox>
                  </div>
                </CustomBox>
              </CustomContainer>
              <Stroke colorValue={lightestGray} />
            </DesktopOnly>
            <div className="mb-8" id="resultsContainer">
              <CustomContainer>
                <CustomRow>
                  {!hideDesktopFilters && (
                    <CollapsibleCol
                      xs={12}
                      lg={3}
                      collapsed={ui.sidebarCollapsed}
                      className="sticky top-0 items-start"
                    >
                      {!isMobile && (
                        <DesktopOnlyJs lg>
                          <div className="sticky top-0 pt-1 pr-8 mt-1">
                            <FilterSidebarItems />
                          </div>
                        </DesktopOnlyJs>
                      )}
                    </CollapsibleCol>
                  )}
                  <CustomCol
                    xs={12}
                    lg={ui.sidebarCollapsed || hideDesktopFilters ? 12 : 9}
                  >
                    <CustomBox
                      mt={3}
                      mb={2}
                      flex="row"
                      justifyContent="space-between"
                      className={classes.resultsHeadBar}
                    >
                      <div className={classes.resultsChips}>
                        <SwitchFilters />
                      </div>

                      <DesktopOnly>
                        <div style={{ minHeight: 16 }} className="pt-4">
                          <ResultsStats />
                        </div>
                      </DesktopOnly>
                    </CustomBox>
                    <div className="sm:mb-4">
                      <CurrentFilterTags />
                    </div>
                    <MobileOnly className="my-4">
                      <div style={{ minHeight: 16 }}>
                        <ResultsStats />
                      </div>
                    </MobileOnly>
                    <NoResults />
                    <div className="my-6" id="resultsList">
                      <AlgoliaSerpHits />
                      <AlgoliaResultsPaginator />
                    </div>
                  </CustomCol>
                </CustomRow>
              </CustomContainer>
            </div>
          </>
        </>
      ) : (
        contents?.data?.bottomContents && (
          <EditorialContents contents={contents.data.bottomContents} />
        )
      )}

      <FiltersModal
        open={ui.filtersModalOpen}
        onClose={() =>
          updateUiState({
            filtersModalOpen: false,
          })
        }
      />
    </>
  )
}

export default AlgoliaSerpContainer
